import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Loader2, Mail, UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';

interface ExternalUser {
  id: string;
  source: string;
  display_name: string;
  email: string | null;
  role_hint: string | null;
  last_seen_at: string;
}

const sourceLabel: Record<string, string> = {
  jira: 'Jira',
  azure_devops: 'Azure DevOps',
  servicenow: 'ServiceNow',
};

const initials = (name: string) =>
  name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');

// Shared by TeamView and StakeholderView's real-tenant branches — both
// read the same external_users table (synced assignee/reporter identity
// from Jira/ADO/ServiceNow), just filtered differently by the caller
// (roleHint prop) since a single synced person can be an "assignee"
// (closer to a team member) or a "reporter" (closer to a stakeholder).
export const SyncedPeopleList = ({
  roleHint,
  emptyMessage,
}: {
  roleHint?: 'assignee' | 'reporter';
  emptyMessage: string;
}) => {
  const { tenantId } = useAuth();
  const { isAdmin } = useUserRole();
  const { inviteUser } = useTenantMembers();
  const [people, setPeople] = useState<ExternalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [memberEmails, setMemberEmails] = useState<Set<string>>(new Set());
  const [invitingId, setInvitingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('external_users')
      .select('id, source, display_name, email, role_hint, last_seen_at')
      .order('last_seen_at', { ascending: false });
    if (tenantId) query = query.eq('tenant_id', tenantId);
    if (roleHint) query = query.eq('role_hint', roleHint);
    const { data, error } = await query;
    if (!error) setPeople((data ?? []) as ExternalUser[]);
    setLoading(false);
  }, [tenantId, roleHint]);

  useEffect(() => { load(); }, [load]);

  // Batch-checked once per load rather than per-row RPC calls — a
  // single is_email_already_member call per email keeps this cheap
  // for the typical small synced-people list, without exposing raw
  // tenant_members rows to the client for a client-side join.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const emails = Array.from(new Set(people.map((p) => p.email).filter(Boolean))) as string[];
      const results = await Promise.all(
        emails.map(async (email) => {
          const { data } = await supabase.rpc('is_email_already_member', { p_email: email });
          return [email, !!data] as const;
        }),
      );
      if (!cancelled) {
        setMemberEmails(new Set(results.filter(([, isMember]) => isMember).map(([email]) => email)));
      }
    })();
    return () => { cancelled = true; };
  }, [people]);

  const handleInvite = async (person: ExternalUser) => {
    if (!person.email) return;
    setInvitingId(person.id);
    try {
      await inviteUser({ email: person.email, role: 'user', mode: 'invite' });
      const { data: { user } } = await supabase.auth.getUser();
      await supabase
        .from('external_users')
        .update({ invited_at: new Date().toISOString(), invited_by: user?.id ?? null })
        .eq('id', person.id);
      toast.success(`Invited ${person.display_name} to Masira`);
      setMemberEmails((prev) => new Set(prev).add(person.email!.toLowerCase()));
    } catch (e) {
      toast.error((e as Error).message || 'Failed to send invite');
    } finally {
      setInvitingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (people.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-muted-foreground masira-fade-in">
        <Users className="w-10 h-10 mb-3 opacity-40" />
        <p className="text-sm">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 masira-fade-in">
      {people.map((person) => {
        const alreadyMember = person.email ? memberEmails.has(person.email.toLowerCase()) : false;
        return (
          <Card key={person.id}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Avatar>
                  <AvatarFallback>{initials(person.display_name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{person.display_name}</p>
                  {person.email && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                      <Mail className="w-3 h-3 shrink-0" /> {person.email}
                    </p>
                  )}
                  <Badge variant="outline" className="text-[10px] mt-1.5">
                    {sourceLabel[person.source] ?? person.source}
                  </Badge>
                </div>
              </div>

              {isAdmin && person.email && !alreadyMember && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mt-3 h-7 text-xs"
                  disabled={invitingId === person.id}
                  onClick={() => handleInvite(person)}
                >
                  {invitingId === person.id ? (
                    <Loader2 className="w-3.5 h-3.5 me-1 animate-spin" />
                  ) : (
                    <UserPlus className="w-3.5 h-3.5 me-1" />
                  )}
                  Suggest inviting to Masira
                </Button>
              )}
              {alreadyMember && (
                <Badge variant="secondary" className="text-[10px] mt-3">Already a member</Badge>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
